import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '@/lib/api';
import { useCurrency } from '@/Hooks/useCurrency';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  type CatalogItem,
  type Menu,
  type MenuItemRow,
  type ScheduleWindow,
  apiError,
  isValidWindowTime,
} from './lib';
import { MenuWizard } from './menus/MenuWizard';
import { ScheduleEditor } from './menus/ScheduleEditor';
import { DEFAULT_WINDOW } from './menus/wizardState';

/** How a chevron press is turned into a network write — see `scheduleReorderCommit`. */
const REORDER_COMMIT_DELAY_MS = 400;
/** The revert highlight's own lifetime — long enough to register as an event,
 *  short enough that it does not linger once the toast has made the point. */
const REVERT_FLASH_MS = 1600;

type SortMode = 'manual' | 'usage';

interface ItemUsage {
  itemId: string;
  orderCount: number;
  dailyAverage: number;
}

type UsageMap = Record<string, ItemUsage>;

export default function MenusTab({ items }: { items: CatalogItem[] }) {
  const { t } = useTranslation();
  // `basePrice` is the catalog's tenant-level price, not a branch override, so
  // it reads in the tenant's base currency even on a branch-scoped menu.
  const { format: formatMoney } = useCurrency(null);
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const canCreate = can('menu.create');
  const canUpdate = can('menu.update');
  const canDelete = can('menu.delete');

  const [menus, setMenus] = useState<Menu[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Menu | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** Manual is the default: it is what the chevrons exist to set, and it is
   *  what a freshly attached item lands in. */
  const [sortMode, setSortMode] = useState<SortMode>('manual');
  const [usageDays, setUsageDays] = useState<7 | 30>(30);
  const [usage, setUsage] = useState<UsageMap | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState<string | null>(null);

  // Rows briefly marked after a reorder that the server refused, so the
  // revert reads as an event that happened rather than a button that silently
  // did nothing.
  const [revertingIds, setRevertingIds] = useState<Set<string>>(new Set());

  /** The next write a debounced chevron burst will send. */
  const pendingOrderRef = useRef<MenuItemRow[] | null>(null);
  const debounceTimerRef = useRef<number | null>(null);
  /** The last ordering the server actually confirmed — what a refused write
   *  reverts to, not just "one step back" from the optimistic move. */
  const lastConfirmedOrderRef = useRef<MenuItemRow[]>([]);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);
    },
    [],
  );

  /** The wizard replaces the tab's whole body while it is up, on both paths. */
  const [creating, setCreating] = useState(false);
  /** The menu being edited, freshly read — see `openEdit`. */
  const [editing, setEditing] = useState<Menu | null>(null);
  const [openingEdit, setOpeningEdit] = useState(false);

  const [schedule, setSchedule] = useState<ScheduleWindow[]>([]);
  const [attachId, setAttachId] = useState('');

  const loadMenus = useCallback(() => {
    setLoading(true);
    api
      .get<{ data: Menu[] }>('/menus', { params: { limit: 100 } })
      .then(({ data }) => {
        const list = data.data ?? [];
        setMenus(list);
        setSelectedId((current) => current ?? list[0]?.id ?? null);
      })
      .catch((e) => toast.error(apiError(e, t('catalog.menus.loadFailed'))))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(loadMenus, [loadMenus]);

  const loadDetail = useCallback(
    (id: string) => {
      setDetailLoading(true);
      setDetailError(null);
      api
        .get<Menu>(`/menus/${id}`)
        .then(({ data }) => {
          setDetail(data);
          setSchedule(Array.isArray(data.schedule) ? data.schedule : []);
          // The source of truth a refused reorder reverts to — refreshed on
          // every real load, since this IS the server's own confirmed order.
          lastConfirmedOrderRef.current = data.items ?? [];
        })
        .catch((e) => {
          // Distinct from "no items on this menu": the request itself failed,
          // and rendering the same empty list for both is exactly the bug the
          // empty-versus-broken rule exists to catch.
          setDetail(null);
          setDetailError(apiError(e, t('catalog.menus.loadFailed')));
        })
        .finally(() => setDetailLoading(false));
    },
    [t],
  );

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const attachable = useMemo(
    () => items.filter((item) => !detail?.items?.some((row) => row.itemId === item.id)),
    [items, detail],
  );

  /**
   * Why `PATCH /menus/:id` would reject this schedule, in the DTO's own terms —
   * null when it would take it. `MenuScheduleWindowDto` marks `days`
   * @ArrayNotEmpty and matches both times against `HH:mm`, and an emptied
   * `<input type="time">` reads back as `''`.
   */
  const scheduleError = schedule.some((window) => window.days.length === 0)
    ? t('catalog.menus.windowNeedsDay')
    : schedule.some(
          (window) => !isValidWindowTime(window.from) || !isValidWindowTime(window.to),
        )
      ? t('catalog.menus.windowNeedsTimes')
      : null;

  /**
   * Edit is the same four-step wizard, seeded from a **fresh** read.
   *
   * `GET /menus/:id` returns the schedule and the attached rows with the menu
   * (`MenusService.findOne` includes `items` ordered by `sortOrder`), so this is
   * the only call seeding costs. It is re-read rather than taken from `detail`
   * because that copy was loaded when the menu was selected and the wizard now
   * computes a *diff* against it: a stale snapshot would quietly re-assert rows
   * someone else has since moved.
   */
  const openEdit = async (id: string) => {
    setOpeningEdit(true);
    try {
      const { data } = await api.get<Menu>(`/menus/${id}`);
      setEditing(data);
    } catch (e) {
      toast.error(apiError(e, t('catalog.menus.loadFailed')));
    } finally {
      setOpeningEdit(false);
    }
  };

  const removeMenu = async (menu: Menu) => {
    // `MenusService.remove` has no guard: the row is deleted and `MenuItem`
    // cascades with it. Nothing stops the *default* menu going, which is the
    // one a guest QR falls back to, so the confirmation has to say that.
    const confirmText =
      t('catalog.menus.deleteConfirm', { name: menu.name }) +
      (menu.isDefault ? `\n\n${t('catalog.menus.deleteDefaultWarning')}` : '');
    if (!(await confirm({ title: confirmText, tone: 'danger' }))) return;
    try {
      await api.delete(`/menus/${menu.id}`);
      toast.success(t('catalog.menus.deleted'));
      setSelectedId(null);
      setDetail(null);
      loadMenus();
    } catch (e) {
      toast.error(apiError(e, t('catalog.menus.saveFailed')));
    }
  };

  const saveSchedule = async () => {
    if (!detail || scheduleError) return;
    try {
      await api.patch(`/menus/${detail.id}`, { schedule });
      toast.success(t('catalog.menus.scheduleSaved'));
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.menus.saveFailed')));
    }
  };

  const attach = async () => {
    if (!detail || !attachId) return;
    try {
      await api.post(`/menus/${detail.id}/items`, {
        itemId: attachId,
        sortOrder: detail.items?.length ?? 0,
      });
      setAttachId('');
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.menus.saveFailed')));
    }
  };

  const updateItem = async (itemId: string, patch: { sortOrder?: number; isVisible?: boolean }) => {
    if (!detail) return;
    try {
      await api.patch(`/menus/${detail.id}/items/${itemId}`, patch);
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.menus.saveFailed')));
    }
  };

  const detachItem = async (itemId: string) => {
    if (!detail) return;
    try {
      await api.delete(`/menus/${detail.id}/items/${itemId}`);
      loadDetail(detail.id);
    } catch (e) {
      toast.error(apiError(e, t('catalog.menus.saveFailed')));
    }
  };

  /** Briefly mark rows after a reorder the server refused, so a chevron press
   *  that fails reads as an event, not a button that silently did nothing. */
  const flashRevert = (ids: string[]) => {
    setRevertingIds((prev) => new Set([...prev, ...ids]));
    window.setTimeout(() => {
      setRevertingIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }, REVERT_FLASH_MS);
  };

  const commitReorder = async () => {
    const order = pendingOrderRef.current;
    const menuId = detail?.id;
    pendingOrderRef.current = null;
    if (!order || !menuId) return;
    try {
      await api.post(`/menus/${menuId}/items/reorder`, {
        items: order.map((row) => ({ itemId: row.itemId, sortOrder: row.sortOrder })),
      });
      lastConfirmedOrderRef.current = order;
    } catch (e) {
      // The revert must be VISIBLE, not a silent snap-back: a toast naming the
      // failure, plus a highlight on the rows that moved, so a chevron press
      // that fails reads as "the server said no" and not as a broken button.
      toast.error(apiError(e, t('catalog.menus.reorderFailed')));
      const reverted = lastConfirmedOrderRef.current;
      setDetail((prev) => (prev ? { ...prev, items: reverted } : prev));
      flashRevert(order.map((row) => row.itemId));
    }
  };

  /**
   * A user tapping a chevron three times in under a second must produce one
   * request, not three — the products screen's own history is a 429 from
   * exactly this shape of burst (31 requests measured for a single drag,
   * against the 120-per-60s global throttle). Each press updates the
   * optimistic order immediately and pushes the debounce out; only the LAST
   * ordering in a burst is ever sent.
   */
  const scheduleReorderCommit = (order: MenuItemRow[]) => {
    pendingOrderRef.current = order;
    if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      void commitReorder();
    }, REORDER_COMMIT_DELAY_MS);
  };

  /** One chevron press, one position. Renumbers the whole menu to a clean
   *  0..n-1 sequence rather than trying to patch a single row's sortOrder,
   *  because `sortOrder` is only meaningful as a complete sequence — see
   *  `ReorderMenuItemsDto` on the backend. */
  const moveItem = (itemId: string, direction: 'up' | 'down') => {
    if (!detail || !canUpdate) return;
    const current = detail.items ?? [];
    const index = current.findIndex((row) => row.itemId === itemId);
    if (index === -1) return;
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= current.length) return; // disabled at the ends

    const swapped = [...current];
    [swapped[index], swapped[swapWith]] = [swapped[swapWith], swapped[index]];
    const renumbered = swapped.map((row, i) => ({ ...row, sortOrder: i }));

    setDetail((prev) => (prev ? { ...prev, items: renumbered } : prev));
    scheduleReorderCommit(renumbered);
  };

  const loadUsage = useCallback(() => {
    if (!detail) return;
    setUsageLoading(true);
    setUsageError(null);
    api
      .get<{ items: ItemUsage[] }>(`/menus/${detail.id}/items/usage`, {
        params: { days: usageDays },
      })
      .then(({ data }) => {
        const map: UsageMap = {};
        for (const row of data.items ?? []) map[row.itemId] = row;
        setUsage(map);
      })
      .catch((e) => setUsageError(apiError(e, t('catalog.menus.usageLoadFailed'))))
      .finally(() => setUsageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id, usageDays, t]);

  useEffect(() => {
    if (sortMode === 'usage') loadUsage();
  }, [sortMode, loadUsage]);

  /**
   * The row order actually rendered. Manual mode renders `detail.items`
   * as-is — that array IS the sortOrder sequence, kept in step locally by
   * `moveItem` — so switching back to it always lands on exactly what the
   * server last confirmed. Usage mode sorts a COPY for display only: it must
   * never write `sortOrder`, since "sorted by how it's selling right now" is
   * not an ordering anyone asked to persist.
   */
  const displayedItems = useMemo(() => {
    const manual = detail?.items ?? [];
    if (sortMode !== 'usage' || !usage) return manual;
    return [...manual].sort((a, b) => {
      const diff = (usage[b.itemId]?.dailyAverage ?? 0) - (usage[a.itemId]?.dailyAverage ?? 0);
      return diff !== 0 ? diff : a.item.name.localeCompare(b.item.name);
    });
  }, [detail?.items, sortMode, usage]);

  /**
   * The wizard replaces the tab body rather than opening over it.
   *
   * Its four frames are drawn as full pages, and a create that spans four
   * screens does not belong in a dialog: the list behind it is not context the
   * operator needs, and a modal that tall loses its footer on a phone. Routing
   * it is the shape the design implies and a decision all five wave-2 groups
   * have to take together — see `MenuWizard`.
   *
   * Editing goes through the same component with the menu attached. Nothing is
   * written until the operator reaches step 4 and presses Save, so leaving is
   * always safe: cancelling an edit has applied no part of the diff, exactly as
   * cancelling a create has posted nothing.
   */
  if (creating || editing) {
    return (
      <MenuWizard
        key={editing?.id ?? 'new'}
        items={items}
        menu={editing ?? undefined}
        // On an edit the question is whether *another* menu could win guest
        // resolution, so this one does not count itself.
        otherMenusExist={menus.some((menu) => menu.id !== editing?.id)}
        onCancel={() => {
          setCreating(false);
          setEditing(null);
        }}
        onDone={(menuId, complete) => {
          setSelectedId(menuId);
          loadMenus();
          loadDetail(menuId);
          // A partial save leaves the wizard up: it is holding the requests the
          // server refused, rebased so that pressing Save again re-sends only
          // those. Closing it throws that away.
          if (complete) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    );
  }

  return (
    <div className="grid gap-4 [&>*]:min-w-0 lg:grid-cols-[18rem_1fr]">
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>{t('catalog.menus.title')}</CardTitle>
          {canCreate && (
            <Button size="sm" onClick={() => setCreating(true)}>
              {t('common.add')}
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingState label={t('common.loading')} />
          ) : menus.length === 0 ? (
            <EmptyState title={t('catalog.menus.empty')} hint={t('catalog.menus.emptyHint')} />
          ) : (
            <ul className="divide-y rounded-lg border">
              {menus.map((menu) => (
                <li key={menu.id}>
                  <button
                    onClick={() => setSelectedId(menu.id)}
                    className={`w-full p-3 text-start text-sm hover:bg-hover ${
                      menu.id === selectedId ? 'bg-accent-soft font-medium text-accent-soft-fg' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate">{menu.name}</span>
                      {menu.isDefault && <Badge tone="brand">{t('catalog.menus.default')}</Badge>}
                    </div>
                    <span className="text-xs text-ink-muted">
                      {menu.branchId ? t('catalog.menus.branchScoped') : t('catalog.menus.tenantWide')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {selectedId && detailLoading ? (
          <Card>
            <CardContent className="p-5">
              <LoadingState label={t('common.loading')} />
            </CardContent>
          </Card>
        ) : selectedId && detailError ? (
          <Card>
            <CardContent className="p-5">
              {/* Distinct from "no menu selected": a request failed, and the
                  fix is a retry, not "pick a different menu". */}
              <ErrorState message={detailError} onRetry={() => loadDetail(selectedId)} />
            </CardContent>
          </Card>
        ) : !detail ? (
          <Card>
            <CardContent className="p-5">
              <EmptyState title={t('catalog.menus.nothingSelected')} />
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-3">
                <div>
                  <CardTitle>{detail.name}</CardTitle>
                  <p className="text-xs text-ink-muted">
                    {detail.branch?.name ?? t('catalog.menus.tenantWide')} ·{' '}
                    {t(detail.isActive ? 'catalog.items.active' : 'catalog.items.inactive')}
                  </p>
                </div>
                <div className="flex gap-2">
                  {canUpdate && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={openingEdit}
                      onClick={() => void openEdit(detail.id)}
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="danger" onClick={() => removeMenu(detail)}>
                      {t('common.delete')}
                    </Button>
                  )}
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold">{t('catalog.menus.schedule')}</h4>
                  {canUpdate && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSchedule([...schedule, { ...DEFAULT_WINDOW }])}
                      >
                        {t('catalog.menus.addWindow')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveSchedule}
                        disabled={!!scheduleError}
                        title={scheduleError ?? undefined}
                      >
                        {t('catalog.menus.saveSchedule')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Why the save is unavailable, said out loud next to it. */}
                {scheduleError && <p className="text-xs text-red-600 dark:text-red-400">{scheduleError}</p>}

                {/* The same editor the create wizard's step 2 uses, so the
                    midnight-crossing and never-opens warnings cannot exist on
                    one path and not the other. */}
                <ScheduleEditor
                  schedule={schedule}
                  disabled={!canUpdate}
                  onChange={setSchedule}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle>{t('catalog.menus.items')}</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Read, not write: visible at `menu.view` too, since choosing
                      how to VIEW the list is not the action `menu.update` gates. */}
                  <Select
                    className="w-44"
                    value={sortMode}
                    onChange={(e) => setSortMode(e.target.value as SortMode)}
                    aria-label={t('catalog.menus.sortBy')}
                  >
                    <option value="manual">{t('catalog.menus.sortManual')}</option>
                    <option value="usage">{t('catalog.menus.sortUsage')}</option>
                  </Select>
                  {sortMode === 'usage' && (
                    <Select
                      className="w-32"
                      value={String(usageDays)}
                      onChange={(e) => setUsageDays(Number(e.target.value) === 7 ? 7 : 30)}
                      aria-label={t('catalog.menus.usageWindow')}
                    >
                      <option value="7">{t('catalog.menus.usageWindowDays', { count: 7 })}</option>
                      <option value="30">{t('catalog.menus.usageWindowDays', { count: 30 })}</option>
                    </Select>
                  )}
                  {canUpdate && (
                    <div className="flex items-center gap-2">
                      <Select
                        className="w-56"
                        value={attachId}
                        onChange={(e) => setAttachId(e.target.value)}
                        aria-label={t('catalog.menus.attachItem')}
                      >
                        <option value="">{t('catalog.menus.chooseItem')}</option>
                        {attachable.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name}
                          </option>
                        ))}
                      </Select>
                      <Button
                        size="sm"
                        onClick={attach}
                        // `AttachMenuItemDto.itemId` is @IsNotEmpty; an empty
                        // picker means every catalog item is already on the menu.
                        disabled={!attachId}
                        title={
                          attachId
                            ? undefined
                            : attachable.length
                              ? t('catalog.menus.chooseItem')
                              : t('catalog.menus.allItemsAttached')
                        }
                      >
                        {t('catalog.menus.attachItem')}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {(detail.items ?? []).length === 0 ? (
                  // "Nothing attached yet" — different from a failed usage read
                  // below, and offers the action that fixes it (attach above).
                  <EmptyState title={t('catalog.menus.noItems')} hint={t('catalog.menus.noItemsHint')} />
                ) : sortMode === 'usage' && usageLoading ? (
                  <LoadingState label={t('common.loading')} />
                ) : sortMode === 'usage' && usageError ? (
                  // The menu HAS items; it is the daily-uses read that failed, so
                  // this must not render as "no items on this menu".
                  <ErrorState message={usageError} onRetry={loadUsage} />
                ) : (
                  <TableWrap className="rounded-none border-0">
                    <Table>
                      <THead>
                        <tr>
                          <TH>{t('catalog.items.name')}</TH>
                          <TH>{t('catalog.items.basePrice')}</TH>
                          <TH>
                            {sortMode === 'usage'
                              ? t('catalog.menus.dailyUses')
                              : t('catalog.items.sortOrder')}
                          </TH>
                          <TH>{t('catalog.menus.visibility')}</TH>
                          <TH className="text-end">{t('catalog.items.actions')}</TH>
                        </tr>
                      </THead>
                      <tbody>
                        {displayedItems.map((row, index) => (
                          <TR
                            key={row.itemId}
                            className={
                              revertingIds.has(row.itemId)
                                ? 'bg-red-50 transition-colors dark:bg-red-950/40'
                                : undefined
                            }
                          >
                            <TD>
                              <div className="font-medium">{row.item.name}</div>
                              {row.item.isSoldOut && (
                                <Badge tone="danger">{t('catalog.items.soldOut')}</Badge>
                              )}
                            </TD>
                            <TD>{formatMoney(row.item.basePrice)}</TD>
                            <TD>
                              {sortMode === 'usage' ? (
                                <span className="tabular-nums">
                                  {usage?.[row.itemId]?.dailyAverage ?? 0}
                                </span>
                              ) : canUpdate ? (
                                /**
                                 * Vertical list, so up/down do NOT flip in RTL —
                                 * unlike an icon that encodes horizontal direction
                                 * (back/forward), "up" and "down" mean the same
                                 * thing regardless of reading direction. Only a
                                 * HORIZONTAL reorder control would need a flip.
                                 */
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    disabled={index === 0}
                                    aria-label={t('catalog.menus.moveUp', { name: row.item.name })}
                                    onClick={() => moveItem(row.itemId, 'up')}
                                  >
                                    <ChevronUp className="h-4 w-4" aria-hidden />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    disabled={index === displayedItems.length - 1}
                                    aria-label={t('catalog.menus.moveDown', { name: row.item.name })}
                                    onClick={() => moveItem(row.itemId, 'down')}
                                  >
                                    <ChevronDown className="h-4 w-4" aria-hidden />
                                  </Button>
                                </div>
                              ) : (
                                // No `menu.update`: a chevron here would be a
                                // button that always answers 403, so the position
                                // is shown as data instead of offered as an action.
                                <span className="tabular-nums text-ink-muted">{index + 1}</span>
                              )}
                            </TD>
                            <TD>
                              <Button
                                size="sm"
                                variant={row.isVisible ? 'secondary' : 'outline'}
                                disabled={!canUpdate}
                                onClick={() => updateItem(row.itemId, { isVisible: !row.isVisible })}
                              >
                                {t(row.isVisible ? 'catalog.menus.visible' : 'catalog.menus.hidden')}
                              </Button>
                            </TD>
                            <TD className="text-end">
                              {canUpdate && (
                                <Button
                                  size="sm"
                                  variant="danger"
                                  onClick={() => detachItem(row.itemId)}
                                >
                                  {t('catalog.items.detach')}
                                </Button>
                              )}
                            </TD>
                          </TR>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
